import { createManualPass, sortManualPasses } from './manualStatusConfigurationPasses';
import {
  formatManualJobSelectValue,
  MANUAL_WELDING_MACHINE_ID,
} from './manualJobNumberFormat';
import type { ManualStatusConfigurationValue } from './ManualStatusConfiguration';

export interface ManualStatusConfigurationApiPass {
  name: string;
  current_min?: number | null;
  current_max?: number | null;
  volts_min?: number | null;
  volts_max?: number | null;
  wire_speed_min?: number | null;
  wire_speed_max?: number | null;
  travel_speed_min?: number | null;
  travel_speed_max?: number | null;
  heat_min?: number | null;
  heat_max?: number | null;
}

export interface ManualStatusConfigurationApiRecord {
  job_number: string;
  welding_system?: string;
  slope_in?: number;
  slope_out?: number;
  minimum_weld_time?: number | null;
  status_calculation_level?: string;
  selected_parameter_list?: Record<string, boolean>;
  passes?: ManualStatusConfigurationApiPass[];
}

export interface ManualStatusConfigurationApiResponse {
  jobNumbers: string[];
  configurations: ManualStatusConfigurationApiRecord[];
}

export type ManualStatusJobOption = {
  value: string;
  label: string;
  weldingSystem: string;
};

const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

export const mapApiConfigurationToUi = (
  config: ManualStatusConfigurationApiRecord | null | undefined
): ManualStatusConfigurationValue | null => {
  if (!config) return null;

  return {
    jobNumber: config.job_number,
    weldingSystem: MANUAL_WELDING_MACHINE_ID,
    slopeTimeIn: toDisplayValue(config.slope_in ?? 0),
    slopeTimeOut: toDisplayValue(config.slope_out ?? 0),
    minimumWeldTime: toDisplayValue(config.minimum_weld_time ?? ''),
    enabledParameters: config.selected_parameter_list ?? {},
    passes: sortManualPasses(
      uniquePassesByGroupName(
        (config.passes ?? []).map((pass) => {
          const manualPass = createManualPass(pass.name);
          manualPass.limits = {
            current: {
              min: toDisplayValue(pass.current_min),
              max: toDisplayValue(pass.current_max),
            },
            volts: {
              min: toDisplayValue(pass.volts_min),
              max: toDisplayValue(pass.volts_max),
            },
            wire_speed: {
              min: toDisplayValue(pass.wire_speed_min),
              max: toDisplayValue(pass.wire_speed_max),
            },
            travel_speed: {
              min: toDisplayValue(pass.travel_speed_min),
              max: toDisplayValue(pass.travel_speed_max),
            },
            heat: {
              min: toDisplayValue(pass.heat_min),
              max: toDisplayValue(pass.heat_max),
            },
          };
          return manualPass;
        })
      )
    ),
  };
};

const parseNumeric = (value: string): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const mapUiConfigurationToApi = (
  value: ManualStatusConfigurationValue
): ManualStatusConfigurationApiRecord => ({
  job_number: value.jobNumber,
  welding_system: MANUAL_WELDING_MACHINE_ID,
  slope_in: parseNumeric(value.slopeTimeIn) ?? 0,
  slope_out: parseNumeric(value.slopeTimeOut) ?? 0,
  minimum_weld_time: parseNumeric(value.minimumWeldTime),
  status_calculation_level: 'Pass Level',
  selected_parameter_list: value.enabledParameters,
  passes: sortManualPasses(uniquePassesByGroupName(value.passes)).map((pass) => ({
    name: pass.name.trim(),
    current_min: parseNumeric(pass.limits.current?.min ?? ''),
    current_max: parseNumeric(pass.limits.current?.max ?? ''),
    volts_min: parseNumeric(pass.limits.volts?.min ?? ''),
    volts_max: parseNumeric(pass.limits.volts?.max ?? ''),
    wire_speed_min: parseNumeric(pass.limits.wire_speed?.min ?? ''),
    wire_speed_max: parseNumeric(pass.limits.wire_speed?.max ?? ''),
    travel_speed_min: parseNumeric(pass.limits.travel_speed?.min ?? ''),
    travel_speed_max: parseNumeric(pass.limits.travel_speed?.max ?? ''),
    heat_min: parseNumeric(pass.limits.heat?.min ?? ''),
    heat_max: parseNumeric(pass.limits.heat?.max ?? ''),
  })),
});

export const mapApiResponseToJobNumbers = (
  response: ManualStatusConfigurationApiResponse | null | undefined
): ManualStatusJobOption[] =>
  (response?.configurations ?? []).map((config) => ({
    value: config.job_number,
    label: formatManualJobSelectValue(MANUAL_WELDING_MACHINE_ID, config.job_number),
    weldingSystem: MANUAL_WELDING_MACHINE_ID,
  }));

export const findApiConfigurationForJob = (
  response: ManualStatusConfigurationApiResponse | null | undefined,
  jobNumber: string
) =>
  response?.configurations?.find(
    (config) =>
      config.job_number.trim().toLowerCase() === jobNumber.trim().toLowerCase()
  ) ?? null;

const LEGACY_MANUAL_STATUS_MARKER = 'MANUAL_STATUS';

export type LimitsDataForManualStatus = {
  allDataSet?: Array<Record<string, unknown>>;
  weldDetails?: Array<Record<string, unknown>>;
};

const normalizeWeldingSystem = (value?: unknown): string =>
  String(value ?? MANUAL_WELDING_MACHINE_ID)
    .trim()
    .toLowerCase()
    .replace(/\W/g, '');

const isBlankPipeName = (pipeName?: unknown): boolean =>
  pipeName == null || pipeName === '' || pipeName === 'null';

type LegacyPassNamesMeta = {
  passNames?: string[];
  minimum_weld_time?: number | null;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parsePassNamesList = (raw?: unknown): string[] => {
  if (!raw) return [];

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((name) => String(name).trim()).filter(Boolean);
      }
      if (parsed && typeof parsed === 'object') {
        const meta = parsed as LegacyPassNamesMeta;
        if (Array.isArray(meta.passNames)) {
          return meta.passNames.map((name) => String(name).trim()).filter(Boolean);
        }
      }
    } catch {
      if (raw.includes(',')) {
        return raw.split(',').map((name) => name.trim()).filter(Boolean);
      }
      const trimmed = raw.trim();
      return trimmed ? [trimmed] : [];
    }
    return [];
  }

  if (typeof raw === 'object' && raw !== null) {
    const meta = raw as LegacyPassNamesMeta;
    if (Array.isArray(meta.passNames)) {
      return meta.passNames.map((name) => String(name).trim()).filter(Boolean);
    }
  }

  return [];
};

const parseLegacyMinimumWeldTime = (raw?: unknown): number | null => {
  if (!raw || typeof raw !== 'string') return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return toNumberOrNull((parsed as LegacyPassNamesMeta).minimum_weld_time);
    }
  } catch {
    return null;
  }

  return null;
};

const parseSelectedParameterList = (raw: unknown): Record<string, boolean> => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, boolean>) : {};
};

export const isManualStatusWeldParameter = (row: Record<string, unknown>): boolean => {
  if (normalizeWeldingSystem(row.welding_system) !== MANUAL_WELDING_MACHINE_ID) {
    return false;
  }
  if (row.parsed_json_filekey) {
    return false;
  }
  if (isBlankPipeName(row.pipe_name)) {
    return true;
  }
  return parsePassNamesList(row.pass_names).length > 0;
};

export const isManualStatusLimitRow = (
  row: Record<string, unknown>,
  weldingSystem = MANUAL_WELDING_MACHINE_ID
): boolean => {
  if (row.m500_zone === LEGACY_MANUAL_STATUS_MARKER) {
    return true;
  }

  return (
    isBlankPipeName(row.pipe_name) &&
    normalizeWeldingSystem(row.welding_system) === weldingSystem
  );
};

const mapLimitRowToPass = (row: Record<string, unknown>): ManualStatusConfigurationApiPass => ({
  name: String(row.pass_group_name || row.zone_name || row.pass_name || ''),
  current_min: toNumberOrNull(row.current_min),
  current_max: toNumberOrNull(row.current_max),
  volts_min: toNumberOrNull(row.volts_min),
  volts_max: toNumberOrNull(row.volts_max),
  wire_speed_min: toNumberOrNull(row.wire_speed_min),
  wire_speed_max: toNumberOrNull(row.wire_speed_max),
  travel_speed_min: toNumberOrNull(row.travel_speed_min),
  travel_speed_max: toNumberOrNull(row.travel_speed_max),
  heat_min: toNumberOrNull(row.heat_min),
  heat_max: toNumberOrNull(row.heat_max),
});

/** Strip trailing " 1", " 2", … so Fill 1 / Fill 2 collapse to Fill for save/UI columns. */
export const toManualPassGroupName = (name: string): string => {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return '';
  const numbered = trimmed.match(/^(.+?)\s+(\d+)$/);
  if (numbered) return numbered[1].trim();
  return trimmed;
};

/** Keep one pass per pass group (Fill 1 / Fill 2 → one Fill). Last row wins. */
export const uniquePassesByGroupName = <T extends { name: string }>(passes: T[]): T[] => {
  const byName = new Map<string, T>();
  passes.forEach((pass) => {
    const groupName = toManualPassGroupName(pass.name);
    const key = groupName.toLowerCase();
    if (!key) return;
    byName.set(key, { ...pass, name: groupName });
  });
  return Array.from(byName.values());
};

export const uniquePassNames = (names: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  names.forEach((name) => {
    const trimmed = String(name ?? '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(trimmed);
  });
  return unique;
};

/** Builds manual status configuration from get-limits (`allDataSet` + `weldDetails`). */
export const mapLimitsDataToManualStatusConfiguration = (
  limitsData: LimitsDataForManualStatus | null | undefined
): ManualStatusConfigurationApiResponse => {
  if (!limitsData) {
    return { jobNumbers: [], configurations: [] };
  }

  const allDataSet = limitsData.allDataSet ?? [];
  const weldDetails = limitsData.weldDetails ?? [];
  const manualWeldParams = weldDetails.filter(isManualStatusWeldParameter);

  if (manualWeldParams.length === 0) {
    return { jobNumbers: [], configurations: [] };
  }

  const configurations: ManualStatusConfigurationApiRecord[] = [];

  manualWeldParams.forEach((weldParam) => {
    if (!weldParam.job_number) {
      return;
    }

    const jobNumber = String(weldParam.job_number);
    const manualLimitRows = allDataSet.filter(
      (row) =>
        row.job_number === jobNumber && isManualStatusLimitRow(row, MANUAL_WELDING_MACHINE_ID)
    );

    // After save, limits rows are keyed by unique pass_group_name — never duplicate columns.
    const passesFromLimits = uniquePassesByGroupName(
      manualLimitRows.map(mapLimitRowToPass).filter((pass) => pass.name.trim())
    );

    const legacyPassNames = uniquePassNames(parsePassNamesList(weldParam.pass_names));
    // Unique pass_group_name from limits is canonical after save; also keep any extra
    // names from pass_names so nothing is dropped on reload.
    const passNames = uniquePassNames([
      ...passesFromLimits.map((pass) => pass.name),
      ...legacyPassNames,
    ]);

    if (passNames.length === 0) {
      return;
    }

    const passes = sortManualPasses(
      passNames.map((name) => {
        const matched = passesFromLimits.find(
          (pass) => pass.name.trim().toLowerCase() === name.trim().toLowerCase()
        );
        return matched ? { ...matched, name } : { name };
      })
    );

    configurations.push({
      job_number: jobNumber,
      welding_system: MANUAL_WELDING_MACHINE_ID,
      slope_in: toNumberOrNull(weldParam.slope_in_time) ?? 0,
      slope_out: toNumberOrNull(weldParam.slope_out_time) ?? 0,
      minimum_weld_time:
        toNumberOrNull(weldParam.minimum_weld_time) ??
        parseLegacyMinimumWeldTime(weldParam.pass_names),
      status_calculation_level: String(weldParam.statusCalculationLevel ?? 'Pass Level'),
      selected_parameter_list: parseSelectedParameterList(weldParam.selected_parameter_list),
      passes,
    });
  });

  configurations.sort((a, b) => a.job_number.localeCompare(b.job_number));

  return {
    jobNumbers: configurations.map((config) => config.job_number),
    configurations,
  };
};

/**
 * Save payload from the UI selection (one entry per unique pass_group_name).
 * - Unchecked groups are omitted so the API removes only those groups.
 * - Server updates every DB row that shares a selected pass_group_name
 *   (e.g. one Fill column → updates Fill 1, Fill 2, …).
 */
export const buildManualStatusSavePayload = (
  value: ManualStatusConfigurationValue,
  _limitsData?: LimitsDataForManualStatus | null
): ManualStatusConfigurationApiRecord => mapUiConfigurationToApi(value);
